/**
 * Axios-based HTTP client for Laravel /rms-api: JSON CRUD, shared headers, error modal hooks,
 * binary download POSTs via {@link HttpService.postDownload}, and blob error-body parsing when
 * responses use `responseType: 'blob'`.
 */
// eslint-disable-next-line
import { injectable } from 'inversify';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import errorModalStore from '../components/error-modal/error-modal-store';
import { uuid } from './helpers';

enum Method {
	GET = 'GET',
	POST = 'POST',
	PATCH = 'PATCH',
	PUT = 'PUT',
	DELETE = 'DELETE'
}

export interface IApiResponse<Type> {
	payload: Type;
}

enum ContentType {
	JSON = 'application/json',
}

interface RequestParams {
    silent?: boolean;
}

export interface HttpRequestOptions extends RequestParams {
	/** Overrides default `Accept` (JSON requests default to `application/json`). */
	accept?: string;
	/** Merged into the axios config after method, headers, data, and `withCredentials`. */
	axiosOverrides?: Partial<AxiosRequestConfig>;
	/** When true, resolves with the full `AxiosResponse` instead of `response.data`. */
	returnFullResponse?: boolean;
}

export interface IHttpService {
	get: <T>(path: string, queryParams?: Record<string, any>, options?: RequestParams) => Promise<T>;
	post: <T>(path: string, data?: any) => Promise<T>;
	patch: <T>(path: string, data?: any) => Promise<T>;
	put: <T>(path: string, data?: any) => Promise<T>;
	remove: <T>(path: string, data?: any) => Promise<T>;
	/** POST with JSON body; response is read as `blob` and returned with headers (e.g. file download vs JSON message). */
	postDownload: (path: string, data?: any, options?: HttpRequestOptions) => Promise<AxiosResponse<Blob>>;
	setCustomHeaders: (headers: Record<string, string>) => void;
	getBaseUrl: () => string | undefined;
}

@injectable()
class HttpService implements IHttpService {
	private baseUrl: string | undefined;
	private withCredentials: boolean;
	private customHeaders: Record<string, string> = {};

	constructor(baseURL: string | undefined, withCredentials: boolean, private useErrorModal:boolean = true) {
		this.baseUrl = baseURL;
		this.withCredentials = withCredentials;
	}

	get<T>(path: string, queryParams?: Record<string, any>, options?: RequestParams) {
		return this.makeJsonRequest<T>(Method.GET, `${path}${this.stringifyParams(queryParams)}`, null, options);
	};

	post<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.POST, path, data, params);
	};

	patch<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.PATCH, path, data, params);
	};

	put<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.PUT, path, data, params);
	};

	remove<T>(path: string, data?: any, params?: RequestParams) {
		return this.makeJsonRequest<T>(Method.DELETE, path, data, params);
	};

	postDownload(path: string, data?: any, options?: HttpRequestOptions): Promise<AxiosResponse<Blob>> {
		return this.makeRequest(
			Method.POST,
			ContentType.JSON,
			path,
			data,
			{
				...options,
				accept: options?.accept ?? '*/*',
				axiosOverrides: {
					...options?.axiosOverrides,
					responseType: 'blob',
				},
				returnFullResponse: true,
			},
		) as Promise<AxiosResponse<Blob>>;
	}

	public getBaseUrl(): string | undefined {
		return this.baseUrl;
	}

	public setCustomHeaders(headers: Record<string, string>): void {
		this.customHeaders = headers;
	}

	/**
	 * When `responseType` is `blob`, Axios exposes JSON bodies as a `Blob`.
     * Uses `Content-Type` and text-then-JSON.parse pattern to get response data.
	 */
	public async parseBlobResponseIntoJSON(response: AxiosResponse): Promise<any> {
		const { data } = response;
		if (!(data instanceof Blob)) {
			return data;
		}

		const contentType = String(response.headers['content-type'] ?? '');

		const text = await data.text();
		if (!text.trim()) {
			return {};
		}

		const isJsonContentType = contentType.includes('application/json');
		if (!isJsonContentType) {
            return {};
		}

		try {
			return JSON.parse(text);
		}
        catch {
			return {};
		}
	}

	private makeJsonRequest<T>(type: Method, path: string, data?: any, options?: HttpRequestOptions): Promise<T> {
		return this.makeRequest<T>(type, ContentType.JSON, path, data, options) as Promise<T>;
	};

	private async makeRequest<T>(
		type: Method,
		contentType: ContentType,
		path: string,
		data?: any,
		options?: HttpRequestOptions
	): Promise<T | AxiosResponse> {
		const headers: Record<string, string> = {
			Accept: options?.accept ?? ContentType.JSON,
			'Content-Type': 'application/json',
			'X-Request-Id': generateContextId(),
			...this.customHeaders
		};
		const body = contentType === ContentType.JSON ? data && JSON.stringify(data) : data;
		try {
			const config: AxiosRequestConfig = {
				method: type,
				headers,
				data: body,
				...options?.axiosOverrides,
			};
			if (this.withCredentials) {
				config.withCredentials = true;
			}
			const response = await axios(`${this.baseUrl}${path}`, config);
			if (options?.returnFullResponse) {
				return response;
			}
			return response.data as T;
		}
		catch (error: any) {
			if (error.response) {
				// Request made and server responded
				console.error('response', error.response);

                const responseData = await this.parseBlobResponseIntoJSON(error.response);

				if (this.useErrorModal) {
					const validationErrors = responseData?.errors;
					if (validationErrors) {
						for (let i in validationErrors) {
							errorModalStore.add({
								title: validationErrors[i][0] ?? 'Server Error',
								url: formatEndpoint(error),
								code: responseData?.code || error.response.status
							});
						}
					} else {
						errorModalStore.add({
							...responseData,
							title: error.response.statusText ?? 'Server Error',
							url: formatEndpoint(error),
							code: responseData?.code || error.response.status
						});
					}
				}
			} else if (error.request) {
				// The request was made but no response was received
				console.error('request', error.request);
				
				if (this.useErrorModal) {
					errorModalStore.add({
						title: error.message,
						url: formatEndpoint(error),
					});
				}
			} else {
				// Something happened in setting up the request that triggered an Error
				console.error(error);

				if (this.useErrorModal) {
					errorModalStore.add({
						title: error.message,
						url: formatEndpoint(error),
					});	
				}
			}

			throw error;
		}
	};

	private stringifyParams(params: any) {
		if (!params) return '';
		var keyValPairs = [];
		for (var p in params)
			keyValPairs.push(encodeURIComponent(p) + "=" + encodeURIComponent(params[p]));
		return keyValPairs.length ? `?${keyValPairs.join("&")}` : '';
	}

}

function formatEndpoint(error: any) {
	return [
		(error.config?.method ?? '').toUpperCase(),
		error.config?.url,
	].filter(s => !!s).join(' ');
}

function generateContextId() {
	let generatedUuid = uuid();
	generatedUuid = generatedUuid.replace(/-/g, ''); // Remove dashes from UUID
	// Generate a unique context ID for the request
	return `RMS_${generatedUuid}_${Date.now()}`;
}	

export default HttpService;
